import { Box, Stack, Text, HStack, Select } from "@chakra-ui/react";
import {
  KYDSegmentDailyMetric,
  KYDSegment,
} from "common/interfaces/KYDCampaign";
import dayjs from "dayjs";
import { useState } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type SegmentDailyMetricsChartProps = {
  dailyMetrics: KYDSegmentDailyMetric[];
  monthlyMetrics: KYDSegmentDailyMetric[];
  segment: KYDSegment;
};

type SegmentDailyMetricsChartPoint = {
  date: string;
  count: number;
};

type SegmentMetricsMode = "last_30_days" | "last_12_months";

const formatYAxisValue = (value: number) => {
  const absValue = Math.abs(value);

  if (absValue >= 1_000_000) {
    const millions = value / 1_000_000;
    const formatted = Number.isInteger(millions)
      ? millions.toString()
      : millions.toFixed(1).replace(/\.0$/, "");
    return `${formatted}m`;
  }

  if (absValue >= 1_000) {
    const thousands = value / 1_000;
    const formatted = Number.isInteger(thousands)
      ? thousands.toString()
      : thousands.toFixed(1).replace(/\.0$/, "");
    return `${formatted}k`;
  }

  return value.toLocaleString();
};

const CHART_MODES: { key: SegmentMetricsMode; label: string }[] = [
  { key: "last_30_days", label: "Past 30 Days" },
  { key: "last_12_months", label: "Past 12 Months" },
];

const SegmentDailyMetricsChart = ({
  dailyMetrics,
  monthlyMetrics,
  segment,
}: SegmentDailyMetricsChartProps) => {
  const shouldShowChart = segment.type === "dynamic";
  const [mode, setMode] = useState<SegmentMetricsMode>("last_30_days");
  const modeMetrics =
    mode === "last_12_months" ? monthlyMetrics || [] : dailyMetrics || [];

  const data: SegmentDailyMetricsChartPoint[] = modeMetrics.map((metric) => ({
    date: metric.date,
    count: metric.count,
  }));

  return (
    <Stack
      w="100%"
      borderRadius="8px"
      border="1px solid rgba(0, 0, 0, 0.05)"
      px="24px"
      py="12px"
      gap={3}
      boxShadow={"sm"}
    >
      <HStack justifyContent="space-between" alignItems="center">
        <HStack>
          <Text fontSize="xl" fontWeight="bold">
            Contacts
          </Text>
          <Text fontSize="lg">{segment.size?.toLocaleString?.() || 0}</Text>
        </HStack>
        {shouldShowChart && (
          <Select
            size="sm"
            w="170px"
            value={mode}
            onChange={(e) => setMode(e.target.value as SegmentMetricsMode)}
            bg="white"
            borderColor="gray.200"
          >
            {CHART_MODES.map((chartMode) => (
              <option key={chartMode.key} value={chartMode.key}>
                {chartMode.label}
              </option>
            ))}
          </Select>
        )}
      </HStack>

      {shouldShowChart && data.length > 0 ? (
        <Box w="100%" h="280px">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart key={mode} data={data}>
              <defs>
                <linearGradient
                  id="segmentContactsGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#3182CE" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#3182CE" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                style={{ color: "#4A5568", fontSize: "12px" }}
                dataKey="date"
                tickFormatter={(value: string) =>
                  mode === "last_12_months"
                    ? dayjs(value).format("MMM YY")
                    : dayjs(value).format("MMM D")
                }
                tickLine={false}
                axisLine={{ style: { stroke: "#EDF" } }}
              />
              <YAxis
                style={{ color: "#4A5568", fontSize: "12px" }}
                allowDecimals={false}
                tickFormatter={formatYAxisValue}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                labelFormatter={(value: string) =>
                  mode === "last_12_months"
                    ? dayjs(value).format("MMM YY")
                    : dayjs(value).format("MMM D")
                }
                formatter={(value) => [
                  Number(value || 0).toLocaleString(),
                  "Contacts",
                ]}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="none"
                fill="url(#segmentContactsGradient)"
                fillOpacity={1}
                tooltipType="none"
                legendType="none"
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3182CE"
                strokeWidth={2}
                dot={{ r: 0 }}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      ) : shouldShowChart ? (
        <Text fontSize="14px" color="#718096">
          No segment data yet.
        </Text>
      ) : null}
    </Stack>
  );
};

export default SegmentDailyMetricsChart;
